import { useState } from 'react';
import { Upload, Loader2, CheckCircle2, AlertCircle, Shuffle, HelpCircle, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { timesheetsAPI } from '../lib/api';

const STATUS = {
  matched: { label: 'Matched', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: CheckCircle2 },
  swap: { label: 'Swap', cls: 'bg-blue-50 text-blue-700 border-blue-200', Icon: Shuffle },
  unscheduled: { label: 'Unscheduled', cls: 'bg-slate-100 text-slate-600 border-slate-200', Icon: UserCheck },
  same_name_warning: { label: 'Same-name — confirm #', cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: HelpCircle },
  error: { label: 'Error', cls: 'bg-red-50 text-red-700 border-red-200', Icon: AlertCircle },
};

export function TimesheetUploadReview({ job, weekStart, open, onClose, onDone }) {
  const [spreadsheet, setSpreadsheet] = useState(null);
  const [signed, setSigned] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [review, setReview] = useState(null);
  const [excludes, setExcludes] = useState({}); // row_id -> reason
  const [submitting, setSubmitting] = useState(false);

  const reset = () => { setReview(null); setSpreadsheet(null); setSigned(null); setExcludes({}); };
  const close = () => { reset(); onClose(); };

  const doUpload = async () => {
    if (!spreadsheet) { toast.error('Choose the filled timesheet (.xlsx)'); return; }
    setUploading(true);
    try {
      const res = await timesheetsAPI.upload(job.job_id, weekStart, spreadsheet, signed);
      setReview(res.data);
      toast.success(`Uploaded — ${res.data.summary.total} row(s) read`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload rejected');
    } finally {
      setUploading(false);
    }
  };

  const toggleExclude = (rowId, checked) =>
    setExcludes((p) => { const n = { ...p }; if (checked) n[rowId] = n[rowId] || ''; else delete n[rowId]; return n; });

  const doSubmit = async () => {
    const payload = Object.entries(excludes).map(([row_id, reason]) => ({ row_id, reason }));
    if (payload.some((x) => !x.reason.trim())) { toast.error('Each excluded row needs a reason'); return; }
    setSubmitting(true);
    try {
      await timesheetsAPI.submit(job.job_id, weekStart, review.version_id, payload);
      toast.success('Timesheet submitted');
      close();
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  const rows = review?.rows || [];
  const missing = review?.missing_scheduled_shifts || [];
  // Error rows (overlap, inactive/unknown employee, cross-job overlap) can ONLY be
  // fixed by correcting the sheet and re-uploading — never acknowledged.
  const ACK_STATUSES = ['unscheduled', 'swap', 'same_name_warning'];
  const canAck = (s) => ACK_STATUSES.includes(s);
  const errorCount = rows.filter((r) => r.status === 'error').length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="timesheet-upload-dialog">
        <DialogHeader>
          <DialogTitle>Upload timesheet — #{job?.job_number} · week of {weekStart}</DialogTitle>
          <DialogDescription>
            Upload the filled sheet (our ts-v1 or ts-v2 template) and, optionally, a photo or PDF of the signed paper sheet.
          </DialogDescription>
        </DialogHeader>

        {!review ? (
          <div className="space-y-4">
            <div>
              <Label>Filled timesheet (.xlsx)</Label>
              <Input type="file" accept=".xlsx" data-testid="upload-spreadsheet-input"
                     onChange={(e) => setSpreadsheet(e.target.files?.[0] || null)} />
            </div>
            <div>
              <Label>Signed paper sheet (optional — JPG, PNG, HEIC or PDF, max 10 MB)</Label>
              <Input type="file" accept=".jpg,.jpeg,.png,.heic,.pdf" data-testid="upload-signed-input"
                     onChange={(e) => setSigned(e.target.files?.[0] || null)} />
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2" data-testid="review-summary">
              {Object.entries(STATUS).map(([k, v]) => (
                <Badge key={k} variant="outline" className={v.cls} data-testid={`summary-${k}`}>
                  {v.label}: {review.summary[k] || 0}
                </Badge>
              ))}
              <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200">
                Missing scheduled: {missing.length}
              </Badge>
            </div>

            <div className="border rounded-md overflow-hidden" data-testid="review-rows">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="p-2 text-left">Acknowledge</th>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-left">Employee (resolved)</th>
                    <th className="p-2 text-left">Date / Post</th>
                    <th className="p-2 text-left">Times · Hours</th>
                    <th className="p-2 text-left">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const st = STATUS[r.status] || STATUS.error;
                    return (
                      <tr key={r.id} className="border-t align-top" data-testid={`review-row-${r.id}`}>
                        <td className="p-2">
                          {canAck(r.status) ? (
                            <Checkbox checked={!!excludes[r.id]} data-testid={`exclude-${r.id}`}
                                      onCheckedChange={(c) => toggleExclude(r.id, c)} />
                          ) : null}
                        </td>
                        <td className="p-2">
                          <Badge variant="outline" className={st.cls} data-testid={`row-status-${r.id}`}>{st.label}</Badge>
                          {r.over_16h && <div className="text-xs text-amber-600 mt-1">Over 16h</div>}
                        </td>
                        <td className="p-2" data-testid={`row-employee-${r.id}`}>
                          <div className="font-medium">{r.employee_name || '—'}</div>
                          <div className="text-xs text-slate-500">#{r.employee_number || '—'}</div>
                          {r.status === 'swap' && (
                            <div className="text-xs text-blue-600 mt-1">
                              was scheduled: {r.scheduled_employee_name}
                            </div>
                          )}
                        </td>
                        <td className="p-2">
                          <div>{r.date}</div>
                          <div className="text-xs text-slate-500">{r.post}</div>
                        </td>
                        <td className="p-2">
                          <div>{r.time_in}–{r.time_out}{r.next_day ? ' (+1d)' : ''}</div>
                          <div className="text-xs text-slate-500">{r.computed_hours ?? '—'} h</div>
                          {r.variance && (
                            <div className="text-xs text-slate-400">
                              sched {r.variance.scheduled_time_in}–{r.variance.scheduled_time_out}
                            </div>
                          )}
                        </td>
                        <td className="p-2 max-w-xs">
                          {(r.errors || []).length > 0 && (
                            <div className="text-xs text-red-600" data-testid={`row-errors-${r.id}`}>
                              {(r.errors || []).join(' ')}
                            </div>
                          )}
                          {canAck(r.status) && excludes[r.id] !== undefined && (
                            <Input className="mt-1 text-slate-900" placeholder="Note (why this is OK)"
                                   data-testid={`exclude-reason-${r.id}`}
                                   value={excludes[r.id]}
                                   onChange={(e) => setExcludes((p) => ({ ...p, [r.id]: e.target.value }))} />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {missing.length > 0 && (
              <div className="text-sm" data-testid="missing-shifts">
                <div className="font-medium text-slate-700 mb-1">Scheduled shifts with no hours reported</div>
                <ul className="text-xs text-slate-500 space-y-0.5">
                  {missing.map((m) => (
                    <li key={m.scheduled_shift_id}>
                      {m.shift_date} · {m.employee_name} · {m.post} · {m.time_in}–{m.time_out}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {!review ? (
            <Button onClick={doUpload} disabled={uploading} data-testid="upload-submit-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              Upload & review
            </Button>
          ) : (
            <div className="flex items-center gap-3 w-full justify-between">
              <span className="text-xs text-slate-500" data-testid="review-footer-msg">
                {errorCount > 0
                  ? `Fix ${errorCount} error(s) and re-upload before submitting`
                  : 'Ready to submit.'}
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={reset} data-testid="review-reupload-btn">Re-upload</Button>
                <Button onClick={doSubmit} disabled={submitting || errorCount > 0} data-testid="review-submit-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Submit week
                </Button>
              </div>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
